import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { MapProvider } from '../providers/map-provider';
import { BasemapState } from '../states/basemap-state';
import { BasemapConstant, BasemapOptionEntity, BasemapProviderEnum } from '@gishub/domain';
import { BasemapRepository, BasemapLayerFactory } from '@gishub/infrastructure';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import Basemap from '@arcgis/core/Basemap';
import { BingTileLayer } from '@gishub/layers/bing-tile-layer';

@Injectable({ providedIn: 'root' })
export class BasemapNotifier extends BaseNotifier<BasemapState> {
  private _repository: BasemapRepository | null = null;
  private get repository(): BasemapRepository {
    if (!this._repository) this._repository = new BasemapRepository();
    return this._repository;
  }

  constructor(ref: Ref) {
    super(ref, {
      options: [],
      activeId: BasemapConstant.ARCGIS_LIGHT_GRAY,
      search: '',
    });
  }

  override async build(): Promise<void> {
    const [allResult, defaultResult] = await Promise.all([
      this.repository.getAll(),
      this.repository.getDefault(),
    ]);
    if (allResult.success && allResult.data && defaultResult.success && defaultResult.data) {
      this.setState({
        options: allResult.data,
        activeId: defaultResult.data.id,
        search: '',
      });
      this.setActive(defaultResult.data.id);
    }
  }

  setActive(id: string): void {
    const option = this.getStateValue().options.find(o => o.id === id);
    if (!option) return;

    this.setState({ ...this.getStateValue(), activeId: id });

    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map) return;

    const config = BasemapLayerFactory.createConfig(option);

    let layer
    if (config.type === 'vector') {
      layer = new VectorTileLayer({ url: config.urlTemplate, copyright: config.attribution })
    } else if (option.provider === BasemapProviderEnum.BING) {
      layer = new BingTileLayer({
        urlTemplate: config.urlTemplate,
        copyright: config.attribution,
        subDomains: config.subDomains,
      })
    } else {
      layer = new WebTileLayer({
        urlTemplate: config.urlTemplate,
        copyright: config.attribution,
        subDomains: config.subDomains.length > 0 ? config.subDomains : undefined,
      })
    }

    mapState.map.basemap = new Basemap({
      baseLayers: [layer],
      id: option.id,
      title: option.name,
    });
  }

  setSearch(text: string): void {
    this.setState({ ...this.getStateValue(), search: text });
  }

  get filtered(): BasemapOptionEntity[] {
    const s = this.getStateValue();
    if (!s.search) return s.options;
    const q = s.search.toLowerCase();
    return s.options.filter(o => o.name.toLowerCase().includes(q));
  }
}
